import { FC, useState } from 'react';
import Cookies from 'js-cookie';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Types
import { SubscriptionStatus } from 'app/store/Subscriptions/Subscriptions.types';
import SubscriptionItemServiceCodes from 'app/types/SubscriptionItemServiceCodes';
// Selectors
import { selectCollections } from 'app/store/DMSCollections/DMSCollections.selectors';
import { selectSubscriptions } from 'app/store/BillingSubscriptions/BillingSubscriptions.selectors';
// MUI
import { Box, Button, Container, Typography } from '@mui/material';
// Context
import { useUploaderSubmit } from 'app/context/UploadFiles';
// Components
import {
  FilesUploader,
  FilesUploadAlert
} from 'app/components/Uploader';
import { AppLoader } from 'app/components/Utilities';
import SuccessPage from './SuccessPage';

const UploadDocuments: FC = () => {
  const collections = useAppSelector(selectCollections);
  const subscriptions = useAppSelector(selectSubscriptions);

  const trialingSubscription = subscriptions.find(sub => sub.status === SubscriptionStatus.Trialing);
  const pageImportService = trialingSubscription?.items?.find(item => item.service.code === SubscriptionItemServiceCodes.PageImport);
  const trialPeriodNumberOfUnits = pageImportService?.trialPeriodNumberOfUnits || 0;
  const [uploaded, setUploaded] = useState(false);

  const caseId = Number(Cookies.get('caseId'));

  const { onSubmit, isLoading } = useUploaderSubmit(caseId);

  const handleSubmit = async () => {
    const result = await onSubmit();
    if (result === true) {
      setUploaded(true);
      Cookies.remove('caseId');
    }
  }

  if (!collections) return <AppLoader />;
  if (uploaded) return <SuccessPage />;
  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#fff' }}>
      <Container maxWidth="lg" sx={{
        height: '100%',
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
        <Typography sx={{
          fontSize: { xs: 18, sm: 24 },
          fontWeight: 500,
          textAlign: 'center'
        }}>You’re all set! Your trial has started.</Typography>
        <Typography sx={{
          fontSize: { xs: 14, sm: 16 },
          fontWeight: 500,
          textAlign: 'center'
        }}>Welcome aboard! Your account is now active, and your free trial is underway. To experience the full power of our AI-generated reports, start by uploading your records. Once your data is in, we’ll automatically analyze it and start generating your first report which will be emailed to you upon completion.</Typography>
        <FilesUploadAlert />
        <FilesUploader />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography display="block" variant="caption" component="small">{`- The maximum total number of pages for all uploaded files is ${trialPeriodNumberOfUnits} pages.`}</Typography>
            <Typography display="block" variant="caption" component="small">- Password protected PDFs are not accepted.</Typography>
            <Typography display="block" variant="caption" component="small">- Case Chronology might remove some irrelevant pages and then PDF security permissions will not be taken into consideration.</Typography>
            <Typography display="block" variant="caption" component="small">- Pro tip, upload every file in case including medical records, legal documents, depositions, etc.</Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            loading={isLoading}
          >Upload</Button>
        </Box>
      </Container>
    </Box>
  );
}

export default UploadDocuments;
